import { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 as CheckIcon } from "lucide-react";
import SignupSuccess from "./SignupSuccess";

const INTERESTS = ["Philosophy", "Psychology", "Spirituality", "Self-knowledge", "All of it"];

export default function SignupFunnel() {
  const [form, setForm] = useState({ name: "", email: "", interest: "All of it" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/signup", form);
      setDone(true);
      toast.success("Sent! Check your inbox for the free book.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="signup" className="py-24 md:py-32 bg-muted/40 paper-grain relative overflow-hidden">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <p className="font-ui text-[11px] uppercase tracking-[0.35em] text-primary mb-4">A small gift</p>
          <h2 className="font-display text-4xl md:text-5xl text-foreground leading-[1.05] tracking-tight">
            The entire book, <em className="italic text-secondary">free</em>, in your inbox.
          </h2>
          <div className="wobble-divider my-6" />
          <p className="font-body text-lg leading-relaxed text-foreground/85 max-w-md">
            Thirteen brutal, generous truths gathered from psychology, philosophy and the contemplative
            traditions &mdash; arranged into one field guide you can carry with you for the rest of your life.
          </p>
          <ul className="mt-6 space-y-2 font-body text-foreground/80">
            <li className="flex items-start gap-2"><CheckIcon className="h-4 w-4 mt-1 text-primary shrink-0" /> Full PDF, beautifully typeset.</li>
            <li className="flex items-start gap-2"><CheckIcon className="h-4 w-4 mt-1 text-primary shrink-0" /> Quiet letters from NewWaysToday — only when there&apos;s something worth saying.</li>
            <li className="flex items-start gap-2"><CheckIcon className="h-4 w-4 mt-1 text-primary shrink-0" /> Unsubscribe anytime. No noise.</li>
          </ul>
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-lg p-7 md:p-9" data-testid="signup-funnel">
          {done ? (
            <SignupSuccess email={form.email} />
          ) : (
            <form onSubmit={submit} data-testid="signup-form" className="space-y-5">
              <div>
                <Label htmlFor="signup-name" className="font-ui text-xs uppercase tracking-[0.2em] text-muted-foreground">Your name</Label>
                <Input
                  id="signup-name"
                  data-testid="signup-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Friend"
                  className="mt-2 font-ui h-12 bg-background"
                />
              </div>
              <div>
                <Label htmlFor="signup-email" className="font-ui text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</Label>
                <Input
                  id="signup-email"
                  data-testid="signup-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@somewhere.com"
                  className="mt-2 font-ui h-12 bg-background"
                />
              </div>
              <div>
                <Label className="font-ui text-xs uppercase tracking-[0.2em] text-muted-foreground">What draws you here?</Label>
                <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                  <SelectTrigger data-testid="signup-interest" className="mt-2 font-ui h-12 bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERESTS.map((i) => (
                      <SelectItem key={i} value={i} className="font-ui">{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="signup-submit"
                className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-ui text-sm tracking-wide group"
              >
                {loading ? "Sending…" : "Download ENTIRE BOOK Free"}
                <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
              <p className="font-ui text-[11px] text-muted-foreground text-center">
                We email the PDF straight to you. No spam, ever.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
